import { Injectable } from '@angular/core';
import { JwtHelperService } from '@auth0/angular-jwt';
import { ContentDataStoreService } from '../../liabilities/services/content-data-store.service';
import { OnboardingStoreService } from '../../onboarding/services/onboarding-store.service';
import { SelectedAgreementsStoreService } from '../../core/services/selected-agreements-store.service';
import { CheckedLiabilitiesStoreService } from '../../shared/services/checked-liabilities.service';

@Injectable({
  providedIn: 'root',
})
export class TokenService {
  constructor(private jwtHelperService: JwtHelperService,
              private contentDataStoreService: ContentDataStoreService,
              private onboardingStoreService: OnboardingStoreService,
              private selectedAgreementsStoreService: SelectedAgreementsStoreService,
              private checkedLiabilitiesStoreService: CheckedLiabilitiesStoreService) {
    window.addEventListener('storage', (listener) => {
      if (listener.key === this.REFRESH_TOKEN && !document.hasFocus()) {
        window.location.reload();
      }
    });
  }

  get rememberMe(): boolean {
    return localStorage.getItem(this.REMEMBER_ME) === 'true';
  }

  set rememberMe(value: boolean) {
    localStorage.setItem(this.REMEMBER_ME, String(value));
  }

  get refreshTokenExpired(): boolean {
    const refreshToken = this.getRefreshToken();
    return this.jwtHelperService.isTokenExpired(refreshToken);
  }

  get tokenExpired(): boolean {
    const token = this.getToken();
    return this.jwtHelperService.isTokenExpired(token);
  }

  private readonly ACCESS_TOKEN = 'access_token';
  private readonly IMPERSONATOR = 'impersonator';
  private readonly REFRESH_TOKEN = 'refresh_token';
  private readonly REMEMBER_ME = 'remember_me';
  private readonly MOBILE_APP = 'mobile_app';

  public getToken(): string {
    return localStorage.getItem(this.ACCESS_TOKEN) || '';
  }

  public getRefreshToken(): string {
    return localStorage.getItem(this.REFRESH_TOKEN);
  }

  public setToken(token: string): void {
    const info = this.isImpersonated(token);
    if (info.on) {
      localStorage.setItem(this.IMPERSONATOR, info.by);
    } else {
      localStorage.removeItem(this.IMPERSONATOR);
    }
    localStorage.setItem(this.ACCESS_TOKEN, token);
  }

  public isImpersonalization() {
    return localStorage.getItem(this.IMPERSONATOR) != null
  }

  public setRefreshToken(refreshToken: string): void {
    localStorage.setItem(this.REFRESH_TOKEN, refreshToken);
  }

  public setMobileApp(isMobile: boolean = false) {
    localStorage.setItem(this.MOBILE_APP, String(isMobile));
  }

  public setRememberMe(rememberMe: boolean = false) {
    localStorage.setItem(this.REMEMBER_ME, String(rememberMe));
  }

  public clearLocalStorageItems(): void {
    localStorage.removeItem(this.ACCESS_TOKEN);
    localStorage.removeItem(this.REFRESH_TOKEN);
    localStorage.removeItem(this.REMEMBER_ME);
    localStorage.removeItem(this.MOBILE_APP);
    this.clearLocalStorageSeviceItems();
  }

  public clearLocalStorageSeviceItems(): void {
    this.contentDataStoreService.clear();
    this.selectedAgreementsStoreService.clear();
    this.onboardingStoreService.clear();
    this.checkedLiabilitiesStoreService.clear();
  }

  private parseJwt(token: string): any {
    const [, payload] = token.split('.');
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(decodeURIComponent(Array.prototype.map.call(json, (c: string) =>
      '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2),
    ).join('')));
  }

  private isImpersonated(accessToken: string): { on: boolean, by?: string } {
    if (!accessToken) return { on: false };
    const p = this.parseJwt(accessToken);
    if (p?.impersonated) return { on: true, by: p.impersonator };
    if (p?.act) return { on: true, by: p.act?.sub || p.act?.username };
    return { on: false };
  }

}
